/**
 * WhyChooseSection component - updated to trigger deployment
 */
import { Heart, Target, TrendingUp, ShieldCheck, Star } from "lucide-react";

const WhyChooseSection = () => {
  const reasons = [
    {
      icon: Heart,
      title: "Women-Focused Environment",
      description: "Comfortable and confidential care designed specifically for women's unique needs.",
      color: "bg-pink-100 text-pink-600",
    },
    {
      icon: Target,
      title: "Specialized Diastasis Recti Program",
      description: "Not general physiotherapy – a focused program for post-pregnancy abdominal recovery.",
      color: "bg-blue-100 text-blue-600",
    },
    {
      icon: TrendingUp,
      title: "Result-Oriented Programs",
      description: "Visible improvement within months with our structured rehabilitation approach.",
      color: "bg-emerald-100 text-emerald-600",
    },
    {
      icon: ShieldCheck,
      title: "Safe Alternative to Surgery",
      description: "Structured rehab approach that avoids invasive procedures while delivering results.",
      color: "bg-blue-100 text-blue-600",
    },
  ];

  return (
    <section className="section-spacing bg-gradient-to-b from-stone-50 to-white" data-testid="why-choose-section">
      <div className="section-container">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
            Why Sky PhysioRehab?
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4" data-testid="why-choose-headline">
            What Makes Us Different
          </h2>
        </div>

        {/* Bento Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Main Feature Card */}
          <div className="lg:col-span-2 bg-gradient-to-br from-blue-700 to-blue-900 rounded-3xl p-8 text-white relative overflow-hidden" data-testid="main-feature-card">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
            <div className="relative z-10">
              <div className="flex items-center gap-2 mb-4">
                <Star className="text-yellow-400" fill="currentColor" size={24} />
                <Star className="text-yellow-400" fill="currentColor" size={24} />
                <Star className="text-yellow-400" fill="currentColor" size={24} />
                <Star className="text-yellow-400" fill="currentColor" size={24} />
                <Star className="text-yellow-400" fill="currentColor" size={24} />
              </div>
              <h3 className="text-2xl md:text-3xl font-bold mb-4 text-white">5.0 Rating on Google</h3>
              <p className="text-blue-100 mb-6">Trusted by 7000+ women across Lucknow for post-pregnancy recovery and rehabilitation.</p>
              <div className="flex items-center gap-4">
                <div className="flex -space-x-2">
                  <div className="w-10 h-10 rounded-full bg-pink-200 border-2 border-blue-700"></div>
                  <div className="w-10 h-10 rounded-full bg-blue-200 border-2 border-blue-700"></div>
                  <div className="w-10 h-10 rounded-full bg-emerald-200 border-2 border-blue-700"></div>
                </div>
                <span className="text-blue-200 text-sm">27+ Google Reviews</span>
              </div>
            </div>
          </div>

          {/* Stat Card */}
          <div className="bg-white rounded-3xl p-8 border border-blue-100 stats-card" data-testid="stat-card">
            <div className="text-5xl font-bold text-blue-700 mb-2">17+</div>
            <div className="text-lg font-semibold text-blue-900 mb-2">Years of Excellence</div>
            <p className="text-stone-600 text-sm">Dedicated to women's physiotherapy and rehabilitation in Lucknow.</p>
          </div>

          {/* Feature Cards */}
          {reasons.map((reason, index) => (
            <div
              key={index}
              className="bg-white rounded-2xl p-6 border border-stone-100 hover:border-blue-200 hover:shadow-md transition-all group"
              data-testid={`why-choose-card-${index}`}
            >
              <div className={`w-14 h-14 ${reason.color} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                <reason.icon size={28} />
              </div>
              <h3 className="text-lg font-bold text-blue-900 mb-2">{reason.title}</h3>
              <p className="text-stone-600 text-sm">{reason.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhyChooseSection;
